"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.aggregateEnergySensors = exports.selectPowerSensors = exports.findLatestBranchAmps = exports.parseEventValues = void 0;
const types_1 = require("../../types");
const alertStatus_1 = require("../alertStatus");
const coverage_1 = require("../coverage");
const sensorClassification_1 = require("../sensorClassification");
const sensorInclusion_1 = require("../sensorInclusion");
// Branch CTs always have a tiny noise floor; treat anything below this as "not drawing".
const BRANCH_DRAW_THRESHOLD_AMPS = 0.1;
function parseEventValues(event) {
    if (event.values && typeof event.values === 'object')
        return event.values;
    if (typeof event.values === 'string') {
        try {
            return JSON.parse(event.values);
        }
        catch {
            return {};
        }
    }
    return {};
}
exports.parseEventValues = parseEventValues;
/**
 * Most recent amperage for a given branch input across the fetched events.
 * Power monitor packets are split (A=mains/voltage, B=branch_ct1-8,
 * C=branch_ct9-16), so the latest event may not include the field we want — we
 * walk newest-to-oldest until we find it.
 */
function findLatestBranchAmps(events, inputNumber) {
    const key = `branch_ct${inputNumber}`;
    const sorted = [...events].sort((a, b) => b.timestamp - a.timestamp);
    for (const e of sorted) {
        const values = parseEventValues(e);
        const v = values[key];
        if (typeof v === 'number')
            return v;
        if (typeof v === 'string' && v !== '') {
            const n = Number(v);
            if (!Number.isNaN(n))
                return n;
        }
    }
    return null;
}
exports.findLatestBranchAmps = findLatestBranchAmps;
/**
 * Electrical-category sensor filter, lifted out of the hook's `useMemo`.
 * No hubId filter — see `selectWaterSensors`.
 */
function selectPowerSensors(sensors) {
    return sensors.filter((s) => (0, sensorInclusion_1.isDashboardSensor)(s) && (s.type === types_1.SensorType.POWER || s.type === types_1.SensorType.POWER_ALT));
}
exports.selectPowerSensors = selectPowerSensors;
/**
 * Pure electrical aggregation. Extracted verbatim from
 * `useEnergyAggregation`'s `useMemo` body. `powerSensors` must already be
 * `selectPowerSensors`-filtered.
 */
function aggregateEnergySensors(powerSensors, fetchResults, loading, nowSeconds) {
    if (powerSensors.length === 0) {
        return {
            sensors: [],
            sensorCount: 0,
            overallStatus: types_1.SensorCardStatus.Good,
            needsAttentionCount: 0,
            activeAlertCount: 0,
            offlineCount: 0,
            unknownCount: 0,
            coverage: 'full',
            averages: {
                totalCircuits: 0,
                onlineCircuits: 0,
                offlineCircuits: 0,
                dailyUsage: 0,
                energySavings: 0,
                systemHealthLabel: 'No Sensors',
                systemHealthStatus: types_1.SensorCardStatus.Good,
            },
            loading,
        };
    }
    const sensorSummaries = [];
    let overallStatus = types_1.SensorCardStatus.Good;
    let worstSensor;
    let needsAttentionCount = 0;
    let totalCircuits = 0;
    let onlineCircuits = 0;
    let offlineCircuits = 0;
    let activeAlertCount = 0;
    let offlineSensorCount = 0;
    // Worst status across the LIVE monitors only — colours the health label.
    let liveHealthStatus = types_1.SensorCardStatus.Good;
    powerSensors.forEach((sensor) => {
        const result = fetchResults.get(sensor.id);
        const alerts = result?.alerts ?? [];
        const events = result?.events ?? [];
        const coverage = (0, coverage_1.sensorCoverageState)(sensor, nowSeconds);
        const isOffline = coverage === 'offline';
        // Count circuits from this sensor's branch circuits
        const circuits = sensor.branchCircuits ?? [];
        totalCircuits += circuits.length;
        // HAZARD channel only: active alerts minus the monitor's own SENSOR_INACTIVE
        // row. That row is reported through `coverage` (the coverage badge, and
        // "Not monitored" when every monitor is dark); counting it here as well put
        // the same dead monitor in the hazard badge AND the coverage badge. The
        // detail screens count through the same filter so the two stay in lockstep.
        const hazards = (0, alertStatus_1.hazardAlerts)(alerts);
        activeAlertCount += hazards.length;
        let status = types_1.SensorCardStatus.Good;
        let value = 'Healthy';
        let needsAttention = false;
        let attentionMessage;
        if (isOffline) {
            // Offline wins — stale branch amps must not read as "drawing".
            offlineSensorCount++;
            // Offline, not Error — a coverage gap, not a hazard. No needsAttention /
            // attentionMessage: the coverage badge owns this row. See water.ts.
            status = types_1.SensorCardStatus.Offline;
            value = 'Offline';
        }
        else if (hazards.length > 0) {
            // Determine if surge or low power
            const isSurge = hazards.some((a) => a.alertType?.includes('HIGH_CURRENT') || a.alertType?.includes('SURGE'));
            const isLow = hazards.some((a) => a.alertType?.includes('LOW_CURRENT') || a.alertType?.includes('UNDER'));
            if (isSurge) {
                status = types_1.SensorCardStatus.Error;
                value = 'Power Surge';
                attentionMessage = `${sensor.location || sensor.name}: Power Surge`;
            }
            else if (isLow) {
                status = types_1.SensorCardStatus.Poor;
                value = 'Low Power';
                attentionMessage = `${sensor.location || sensor.name}: Low Power`;
            }
            else {
                status = types_1.SensorCardStatus.Poor;
                value = 'Alert Active';
                attentionMessage = `${sensor.location || sensor.name}: Alert`;
            }
            needsAttention = true;
        }
        // Per-branch "drawing power" check. The power monitor itself is always reporting,
        // so a single sensor-level liveness flag would only ever read all-on or all-off.
        // Instead, walk the configured circuits and look at the latest branch_ct{N} amperage
        // for each one — drawing if above the noise floor. An offline monitor's stale
        // events are skipped: all of its circuits count as not drawing.
        circuits.forEach((c) => {
            if (isOffline || typeof c.powerMonitorInputNumber !== 'number') {
                offlineCircuits++;
                return;
            }
            const amps = findLatestBranchAmps(events, c.powerMonitorInputNumber);
            if (amps !== null && amps > BRANCH_DRAW_THRESHOLD_AMPS) {
                onlineCircuits++;
            }
            else {
                offlineCircuits++;
            }
        });
        const summary = {
            sensorId: sensor.id,
            name: sensor.name || sensor.location || 'Power Monitor',
            location: sensor.location || '',
            typeLabel: 'Power Monitor',
            sensorType: sensor.type,
            status,
            value,
            needsAttention,
            attentionMessage,
            isOffline,
            coverage,
            lastReportedAt: sensor.lastReported,
            alertCount: hazards.length,
        };
        sensorSummaries.push(summary);
        if (needsAttention)
            needsAttentionCount++;
        if (!isOffline)
            liveHealthStatus = (0, sensorClassification_1.worstStatus)(liveHealthStatus, status);
        const newOverall = (0, sensorClassification_1.worstStatus)(overallStatus, status);
        if (newOverall !== overallStatus) {
            overallStatus = newOverall;
            worstSensor = summary;
        }
    });
    // "Healthy" / "N Issues Detected" — same hazard count the detail cards show.
    // Was Efficient/Inefficient, which implied an energy-efficiency judgement the card
    // never actually made. Offline monitors are NOT issues: with one of two dark the
    // label is about the power we can see ("Healthy") and the coverage badge carries
    // the gap; with all dark the label itself becomes "Not monitored".
    const issueCount = activeAlertCount;
    const allDark = powerSensors.length > 0 && offlineSensorCount === powerSensors.length;
    const systemHealthLabel = allDark
        // Home_Electrical/index.tsx string-compares this label to decide the
        // detail header, so it moves in lockstep with the web-hub commit.
        ? 'Not monitored'
        : issueCount === 0
            ? 'Healthy'
            : `${issueCount} ${issueCount === 1 ? 'Issue' : 'Issues'} Detected`;
    // Colour for the label above. Live monitors only — see EnergyAverages docs.
    const systemHealthStatus = allDark ? types_1.SensorCardStatus.Offline : liveHealthStatus;
    // Rolled up from the per-row coverage states — one source of truth.
    const rollup = (0, coverage_1.categoryCoverage)(sensorSummaries);
    return {
        sensors: sensorSummaries,
        sensorCount: powerSensors.length,
        overallStatus,
        needsAttentionCount,
        activeAlertCount,
        offlineCount: rollup.offlineCount,
        unknownCount: rollup.unknownCount,
        coverage: rollup.coverage,
        worstSensor,
        averages: {
            totalCircuits,
            onlineCircuits,
            offlineCircuits,
            dailyUsage: 0,
            energySavings: 0,
            systemHealthLabel,
            systemHealthStatus,
        },
        loading,
    };
}
exports.aggregateEnergySensors = aggregateEnergySensors;
